import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { applyRestoredAnimalLocal } from "@/lib/repo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Trash2, RotateCcw, Beef, Clock } from "lucide-react";
import { formatDateTime, CATEGORY_LABEL } from "@/lib/utils";
import { toast } from "sonner";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";

export default function Trash() {
  const navigate = useNavigate();
  const online = useOnline();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [restoringId, setRestoringId] = useState(null);
  const [restoringBulk, setRestoringBulk] = useState(false);

  const load = () => {
    if (!online) return;
    api.listTrash()
      .then(setItems)
      .catch(() => toast.error("No pude cargar la papelera"));
  };
  useEffect(() => { load(); }, [online]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const restoreOne = async (id) => {
    setRestoringId(id);
    try {
      const r = await api.restoreAnimal(id);
      await applyRestoredAnimalLocal(r.animal);
      if (r.chip_conflicts?.length) {
        toast.warning(`Animal restaurado. ${r.chip_conflicts.length} chip(s) ya estaban en uso y quedaron marcados como dañados — podés vincular uno nuevo desde la ficha.`);
      } else {
        toast.success("Animal restaurado");
      }
      setItems((prev) => prev.filter((a) => a.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude restaurar el animal");
    } finally { setRestoringId(null); }
  };

  const restoreSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setRestoringBulk(true);
    try {
      const r = await api.restoreAnimalsBulk(ids);
      for (const id of r.restored) {
        try { const a = await api.getAnimal(id); await applyRestoredAnimalLocal(a); } catch (_) {}
      }
      toast.success(`${r.total_restored} animal(es) restaurado(s)`);
      if (r.failed?.length) toast.warning(`${r.failed.length} no se pudieron restaurar`);
      setItems((prev) => prev.filter((a) => !ids.includes(a.id)));
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude restaurar la selección");
    } finally { setRestoringBulk(false); }
  };

  const allSelected = useMemo(() => !!items?.length && selected.size === items.length, [items, selected]);
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set((items || []).map((a) => a.id)));
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="trash-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold">Papelera</h1>
          <p className="text-[13px] text-muted-foreground">Los animales eliminados quedan acá 30 días antes de borrarse para siempre.</p>
        </div>
      </div>

      {!online && <OfflineNotice label="La papelera necesita conexión (coordina el freno de seguridad y los backups con el servidor)." />}

      {online && items === null && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-[var(--radius)]" />)}
        </div>
      )}

      {online && items?.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center" data-testid="trash-empty">
            <Trash2 size={28} className="text-muted-foreground mx-auto mb-2" />
            <h3 className="font-editorial text-[20px] tracking-tight">La papelera está vacía</h3>
            <p className="text-[13px] text-muted-foreground mt-1">Los animales que elimines van a aparecer acá.</p>
          </CardContent>
        </Card>
      )}

      {online && items?.length > 0 && (
        <>
          <div className="flex items-center justify-between px-1">
            <label className="flex items-center gap-2 text-[13px] text-muted-foreground cursor-pointer">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="trash-select-all" />
              Seleccionar todo ({items.length})
            </label>
            {selected.size > 0 && (
              <Button size="sm" onClick={restoreSelected} disabled={restoringBulk} data-testid="trash-restore-selected-button">
                <RotateCcw size={14} className="mr-1.5" />
                {restoringBulk ? "Restaurando…" : `Restaurar ${selected.size} seleccionado(s)`}
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="p-0 divide-y divide-border" data-testid="trash-list">
              {items.map((a) => {
                const urgent = a.days_left !== null && a.days_left <= 3;
                return (
                  <div key={a.id} className="p-3 flex items-center gap-3" data-testid={`trash-row-${a.id}`}>
                    <Checkbox
                      checked={selected.has(a.id)}
                      onCheckedChange={() => toggle(a.id)}
                      data-testid={`trash-checkbox-${a.id}`}
                    />
                    <div className="w-9 h-9 rounded-[calc(var(--radius)-0.35rem)] bg-accent text-accent-foreground grid place-items-center shrink-0">
                      <Beef size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-heading font-semibold flex items-center gap-2 flex-wrap">
                        Lomo {a.lomo_number || "—"}
                        <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[a.category] || "Sin categoría"}</Badge>
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground tracking-[0.05em] mt-0.5">
                        {a.chip_code_previous || a.chips?.[0]?.rfid || "sin chip"}
                      </div>
                      <div className="text-[11.5px] text-muted-foreground mt-0.5">
                        Eliminado el {formatDateTime(a.deleted_at)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant={urgent ? "ochre" : "muted"} className="text-[10px] mb-1.5 flex items-center gap-1" data-testid={`trash-days-left-${a.id}`}>
                        <Clock size={10} />
                        {a.days_left != null ? `${a.days_left} día(s)` : "—"}
                      </Badge>
                      <br />
                      <Button
                        variant="outline" size="sm" onClick={() => restoreOne(a.id)}
                        disabled={restoringId === a.id} data-testid={`trash-restore-${a.id}`}
                      >
                        <RotateCcw size={13} className="mr-1" />
                        {restoringId === a.id ? "…" : "Restaurar"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
